// src/features/merchantConfig/hooks/useAllMerchantConfigs.js
//
// Fans out the 12 GETs in parallel on page mount so the sidebar can resolve
// its status dots without waiting for the user to click each category.
//
// Implementation notes:
//   * `useQueries` from TanStack Query is the idiomatic way to express N
//     parallel queries that share retry / cache rules. It internally uses
//     Promise.allSettled-style isolation - one failure does NOT cancel the
//     others.
//   * Each child query shares the SAME `queryKey` shape as `useMerchantConfig`,
//     so when the user clicks a category and `useMerchantConfig` mounts, it
//     gets a cache hit instantly with no extra network call.
//   * We DELIBERATELY do not increase parallelism limits here. The browser
//     will queue these naturally; 12 concurrent same-origin GETs is well
//     within HTTP/2 limits.

import { useQueries } from "@tanstack/react-query";
import { fetchMerchantConfig } from "../api/merchantConfigClient";
import { merchantConfigQueryKey } from "./useMerchantConfig";
import { MERCHANT_CONFIG_CATALOG } from "../lib/configKeyCatalog";

/**
 * Status describing where a single category's GET resolved.
 *
 * @typedef {"loading" | "provisioned" | "notProvisioned" | "error"} CategoryStatus
 */

/**
 * @typedef {Object} CategoryProbeResult
 * @property {string} key                            - enum key (case-sensitive wire value)
 * @property {string} slug
 * @property {string} displayName
 * @property {CategoryStatus} status
 * @property {Record<string, unknown> | null} data   - full envelope (200 only)
 * @property {Object | null} error                   - normalised error (non-200 only)
 */

/**
 * Hook: fires the 12 GETs in parallel and returns a uniform per-category
 * status snapshot plus aggregate counts.
 *
 * @param {{ merchantId: string | null | undefined, enabled?: boolean }} options
 * @returns {{
 *   results: CategoryProbeResult[],
 *   loadedCount: number,
 *   totalCount: number,
 *   isAllResolved: boolean
 * }}
 */
export function useAllMerchantConfigs({ merchantId, enabled = true }) {
  const queries = useQueries({
    queries: MERCHANT_CONFIG_CATALOG.map((entry) => ({
      queryKey: merchantConfigQueryKey(merchantId, entry.key),
      queryFn: ({ signal }) =>
        fetchMerchantConfig({
          merchantId,
          configKey: entry.key,
          signal,
        }),
      enabled: Boolean(enabled && merchantId),
      staleTime: 60_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      // 404 is not retried; 4xx not retried; 5xx + network -> 2 retries.
      retry: (failureCount, error) => {
        if (error?.isNotFound) return false;
        if (error?.status && error.status >= 400 && error.status < 500) return false;
        return failureCount < 2;
      },
      retryDelay: (attemptIndex) => Math.min(1_000 * 2 ** attemptIndex, 8_000),
    })),
  });

  const results = MERCHANT_CONFIG_CATALOG.map((entry, idx) => {
    const q = queries[idx];
    /** @type {CategoryStatus} */
    let status;
    if (!q || q.isLoading || q.isPending) {
      status = "loading";
    } else if (q.isSuccess) {
      status = "provisioned";
    } else if (q.error?.isNotFound) {
      status = "notProvisioned";
    } else {
      status = "error";
    }
    return {
      key: entry.key,
      slug: entry.slug,
      displayName: entry.displayName,
      status,
      data: q?.data ?? null,
      error: q?.error ?? null,
    };
  });

  const loadedCount = results.filter(
    (r) => r.status === "provisioned" || r.status === "notProvisioned" || r.status === "error"
  ).length;
  const totalCount = results.length;
  const isAllResolved = loadedCount === totalCount;

  return {
    results,
    loadedCount,
    totalCount,
    isAllResolved,
  };
}
